import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http'
import { Injectable } from '@angular/core'
import { saveAs } from 'file-saver'
import { orderBy } from 'lodash'
import { BehaviorSubject, catchError, finalize, map, Observable, shareReplay, startWith, Subject, switchMap } from 'rxjs'

import { PDFDownloadLink } from '../data/pdf-download-link'
import { DocumentTypeModel } from '../model'
import { CrudService } from './crud.service'

@Injectable({
  providedIn: 'root',
})
export class FileService {
  public isLoadingDocumentTypes$ = new BehaviorSubject<boolean>(false)
  public isSavingDocumentTypes$ = new BehaviorSubject<boolean>(false)

  private refreshDocumentTypes$ = new Subject<void>()
  public documentTypeError$ = new Subject<string>()

  public documentTypes$ = this.refreshDocumentTypes$.pipe(
    startWith(null),
    switchMap(() => {
      this.isLoadingDocumentTypes$.next(true)
      return this.crudService
        .index(DocumentTypeModel)
        .pipe(
          map((response) => {
            response.sort()
            return response
          }),
          finalize(() => this.isLoadingDocumentTypes$.next(false)),
          shareReplay(1),
        )
    }),
    map((fields) => orderBy(fields, 'label')),
    shareReplay(1),
  )

  constructor(
    public http: HttpClient,
    public crudService: CrudService,
  ) {}

  downloadFile(url: string): Observable<Blob> {
    const headers = new HttpHeaders().set('enctype', 'multipart/form-data')
    return this.http.get(url, { headers, responseType: 'blob' })
  }

  saveAs(pdfDownloadLink: PDFDownloadLink): void {
    if (typeof pdfDownloadLink === 'string') {
      const win = window.open('about:blank', 'documentWindow')
      if (win == null) {
        alert('A popup blocker prevented the display window from opening')
      } else {
        win.document.write(pdfDownloadLink)
      }
    } else {
      const url = pdfDownloadLink.url
      const fileName =
        pdfDownloadLink.pdfFileName || (pdfDownloadLink as any).fileName
      this.downloadFile(url).subscribe(
        (data) => saveAs(data, fileName),
        (err: HttpErrorResponse) => {
          if (err.status === 0) {
            // if we got a 0 status, it either means they lost Internet or there was a CORS request error
            // in either case, attempt to open the PDF in a new window
            const win = window.open(url, 'documentWindow')
            // if a popup blocker failed, then try to direct the web page to the PDF, it should cause a
            // download and not actually navigate the user… but this is all edge case
            if (win == null) {
              window.location.href = url
            }
          } else {
            alert(`Failed to download the requested file, please try again`)
          }
        },
      )
    }
  }

  upsertDocumentType(documentType: DocumentTypeModel) {
    this.isSavingDocumentTypes$.next(true)
    this.documentTypeError$.next(null)

    return (documentType.id ?
      this.crudService.update(documentType) :
      this.crudService.create(documentType))
      .pipe(
        catchError((err) => {
          this.documentTypeError$.next(err.error.message)
          throw err
        }),
        map((response) => {
          this.refreshDocumentTypes$.next()
          return response
        }),
        finalize(() => this.isSavingDocumentTypes$.next(false)),
      )
  }

  deleteDocumentType(type: DocumentTypeModel) {
    this.isSavingDocumentTypes$.next(true)
    return this.crudService.delete(type).pipe(
      catchError((err) => {
        this.documentTypeError$.next(err.error.message)
        throw err
      }),
      map(() => this.refreshDocumentTypes$.next()),
      finalize(() => this.isSavingDocumentTypes$.next(false)),
    )
  }
}
